import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useBillingBalance } from '../../hooks/api/useBilling';
import { Button } from '../ui';
import Logo from '../Logo';

export default function Header() {
  const { isAuthenticated, isAdmin, user, logout, isLoading } = useAuth();
  const { data: balance } = useBillingBalance(isAuthenticated);
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isActivePath = (path: string) => location.pathname === path;

  const navLinkClass = (path: string) =>
    `text-base font-medium transition-colors hover:text-primary ${
      isActivePath(path)
        ? 'text-primary border-b-2 border-primary'
        : 'text-gray-700'
    }`;

  const mobileLinkClass = (path: string) =>
    `block px-4 py-2 text-base font-medium rounded-md transition-colors hover:bg-gray-100 ${
      isActivePath(path) ? 'text-primary bg-gray-50' : 'text-gray-700'
    }`;

  const closeMobileMenu = () => setMobileMenuOpen(false);

  return (
    <header className="sticky top-0 z-40 border-b border-gray-200 bg-white shadow-sm">
      <nav className="container mx-auto px-4 py-2">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center hover:opacity-80 transition-opacity">
            <Logo variant="full" color="full-color" size="lg" className="!w-40 h-auto" />
          </Link>

          <div className="hidden md:flex items-center gap-8">
            {isLoading ? null : isAuthenticated ? (
              <>
                <Link to="/dashboard" className={navLinkClass('/dashboard')}>
                  Dashboard
                </Link>
                <Link to="/permits/search" className={navLinkClass('/permits/search')}>
                  Search
                </Link>
                <Link to="/account" className={navLinkClass('/account')}>
                  Account
                </Link>
                {isAdmin && (
                  <Link to="/admin" className={navLinkClass('/admin')}>
                    Admin
                  </Link>
                )}
                <div className="flex items-center gap-4 ml-4 pl-4 border-l border-gray-200">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-700">
                      {user?.email}
                    </span>
                    {balance?.trial_active &&
                      !balance?.is_admin &&
                      balance?.trial_days_remaining !== null &&
                      balance?.trial_days_remaining !== undefined && (
                        <span
                          className={`text-xs px-2 py-1 rounded-full font-medium ${
                            balance.trial_days_remaining <= 2
                              ? 'bg-orange-100 text-orange-700'
                              : 'bg-blue-100 text-blue-700'
                          }`}
                          title={`${balance.trial_days_remaining} day${
                            balance.trial_days_remaining !== 1 ? 's' : ''
                          } remaining in free trial`}
                        >
                          {balance.trial_days_remaining}d trial
                        </span>
                      )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={logout}
                  >
                    Logout
                  </Button>
                </div>
              </>
            ) : (
              <>
                <Link to="/login" className="flex items-center text-base font-medium text-gray-700 hover:text-primary transition-colors">
                  Login
                </Link>
                <Link to="/register" className="flex items-center">
                  <Button variant="primary" size="md">
                    Get Started
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              type="button"
              className="rounded-md p-2 text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary"
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? (
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile menu panel */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-gray-200 bg-white px-4 py-3 space-y-1">
          {isLoading ? null : isAuthenticated ? (
            <>
              <Link to="/dashboard" className={mobileLinkClass('/dashboard')} onClick={closeMobileMenu}>
                Dashboard
              </Link>
              <Link to="/permits/search" className={mobileLinkClass('/permits/search')} onClick={closeMobileMenu}>
                Search
              </Link>
              <Link to="/account" className={mobileLinkClass('/account')} onClick={closeMobileMenu}>
                Account
              </Link>
              {isAdmin && (
                <Link to="/admin" className={mobileLinkClass('/admin')} onClick={closeMobileMenu}>
                  Admin
                </Link>
              )}
              <div className="pt-2 mt-2 border-t border-gray-100">
                <p className="px-4 py-1 text-sm text-gray-500">{user?.email}</p>
                <button
                  className="block w-full text-left px-4 py-2 text-base font-medium text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                  onClick={() => { closeMobileMenu(); logout(); }}
                >
                  Logout
                </button>
              </div>
            </>
          ) : (
            <>
              <Link to="/login" className={mobileLinkClass('/login')} onClick={closeMobileMenu}>
                Login
              </Link>
              <Link to="/register" className={mobileLinkClass('/register')} onClick={closeMobileMenu}>
                Get Started
              </Link>
            </>
          )}
        </div>
      )}
    </header>
  );
}
